import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, MessageSquare, Send, Clock, Tag,
  FileText, Link as LinkIcon, ChevronDown, ChevronUp, Filter,
  BookOpen, Beaker, FileSearch, Cpu, BarChart3, X, ExternalLink
} from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { UserAvatar } from './UserAvatar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General Research', icon: BookOpen, color: '#6366F1' },
  { value: 'experiment', label: 'Experiment', icon: Beaker, color: '#10B981' },
  { value: 'literature_review', label: 'Literature Review', icon: FileSearch, color: '#F59E0B' },
  { value: 'prototype', label: 'Prototype', icon: Cpu, color: '#EC4899' },
  { value: 'analysis', label: 'Data Analysis', icon: BarChart3, color: '#8B5CF6' }
];

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#6B7280', bg: '#F3F4F6' },
  submitted: { label: 'Submitted', color: '#3B82F6', bg: '#DBEAFE' },
  under_review: { label: 'Under Review', color: '#F59E0B', bg: '#FEF3C7' },
  approved: { label: 'Approved', color: '#10B981', bg: '#D1FAE5' },
  archived: { label: 'Archived', color: '#6B7280', bg: '#E5E7EB' }
};

const ResearchSubmissions = ({ user }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showMySubmissions, setShowMySubmissions] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    tags: [],
    document_links: [],
    status: 'draft'
  });
  const [newTag, setNewTag] = useState('');
  const [newLink, setNewLink] = useState({ title: '', url: '' });

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);
      if (showMySubmissions) params.append('my_submissions', 'true');
      
      const response = await axios.get(`${API_URL}/research-submissions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      toast.error('Failed to load research submissions');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus, showMySubmissions]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Title and description are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingSubmission) {
        await axios.put(`${API_URL}/research-submissions/${editingSubmission.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Research updated successfully');
      } else {
        await axios.post(`${API_URL}/research-submissions`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Research submitted successfully');
      }
      resetForm();
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save research');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this research submission?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/research-submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Research deleted');
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete research');
    }
  };

  const handleEdit = (submission) => {
    setEditingSubmission(submission);
    setFormData({
      title: submission.title,
      description: submission.description,
      category: submission.category,
      tags: submission.tags || [],
      document_links: submission.document_links || [],
      status: submission.status
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingSubmission(null);
    setFormData({
      title: '',
      description: '',
      category: 'general',
      tags: [],
      document_links: [],
      status: 'draft'
    });
    setNewTag('');
    setNewLink({ title: '', url: '' });
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const addLink = () => {
    if (newLink.title.trim() && newLink.url.trim()) {
      setFormData({ ...formData, document_links: [...formData.document_links, newLink] });
      setNewLink({ title: '', url: '' });
    }
  };

  const removeLink = (index) => {
    setFormData({ ...formData, document_links: formData.document_links.filter((_, i) => i !== index) });
  };

  // Comments
  const handleAddComment = async (submissionId) => {
    if (!newComment.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/research-submissions/${submissionId}/comments`, 
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment('');
      toast.success('Comment added');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (submissionId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/research-submissions/${submissionId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comment deleted');
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const filteredSubmissions = submissions.filter(s =>
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getCategoryConfig = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading research submissions...</p>
      </div>
    );
  }

  return (
    <div data-testid="research-submissions">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Beaker size={24} color="#FF9800" />
            Research Submissions
          </h2>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Submit and manage your R&D research work
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="gradient-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
          data-testid="submit-research-btn"
        >
          <Plus size={18} /> Submit Research
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input
            type="text"
            placeholder="Search research..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '14px'
            }}
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <button
          onClick={() => setShowMySubmissions(!showMySubmissions)}
          style={{
            padding: '10px 16px',
            border: `1px solid ${showMySubmissions ? '#FF9800' : 'var(--border-color)'}`,
            borderRadius: '8px',
            background: showMySubmissions ? '#FFF7ED' : 'var(--bg-secondary)',
            color: showMySubmissions ? '#FF9800' : 'var(--text-primary)',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
          data-testid="my-submissions-btn"
        >
          My Submissions
        </button>
      </div>

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <Beaker size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No Research Submissions</h3>
          <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>
            {showMySubmissions ? "You haven't submitted any research yet" : "No research has been submitted yet"}
          </p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="gradient-btn"
            style={{ padding: '12px 24px' }}
          >
            <Plus size={18} style={{ marginRight: '8px' }} /> Submit Your First Research
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredSubmissions.map(submission => {
            const catConfig = getCategoryConfig(submission.category);
            const CatIcon = catConfig.icon;
            const statusConfig = STATUS_CONFIG[submission.status] || STATUS_CONFIG.draft;
            const isExpanded = expandedSubmission === submission.id;
            const isOwner = submission.submitted_by === user?.id;
            const canEdit = isOwner || ['manager', 'ceo', 'superadmin'].includes(user?.role);

            return (
              <div 
                key={submission.id}
                className="card"
                style={{ 
                  padding: '20px',
                  borderLeft: `4px solid ${catConfig.color}`
                }}
                data-testid={`submission-${submission.id}`}
              >
                {/* Submission Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: `${catConfig.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CatIcon size={20} color={catConfig.color} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          {submission.title}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                          <span>{catConfig.label}</span>
                          <span>•</span>
                          <span>by {submission.submitted_by_name}</span>
                          <span>•</span>
                          <Clock size={12} />
                          <span>{new Date(submission.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    {submission.tags && submission.tags.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {submission.tags.map((tag, idx) => (
                          <span key={idx} style={{
                            padding: '2px 10px',
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            fontSize: '11px',
                            color: 'var(--text-secondary)'
                          }}>
                            <Tag size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status & Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: statusConfig.bg,
                      color: statusConfig.color
                    }}>
                      {statusConfig.label}
                    </span>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleEdit(submission)}
                          style={{ padding: '8px', background: 'var(--bg-secondary)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(submission.id)}
                          style={{ padding: '8px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#EF4444' }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => setExpandedSubmission(isExpanded ? null : submission.id)}
                      style={{
                        padding: '8px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <MessageSquare size={16} />
                      <span style={{ fontSize: '12px' }}>{submission.comments?.length || 0}</span>
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                    {/* Description */}
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={16} /> Research Details
                      </h4>
                      <div 
                        style={{ 
                          padding: '16px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '8px',
                          lineHeight: '1.7',
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}
                        dangerouslySetInnerHTML={{ __html: submission.description }}
                      />
                    </div>

                    {/* Document Links */}
                    {submission.document_links && submission.document_links.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <LinkIcon size={16} /> Reference Links
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {submission.document_links.map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 14px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                color: '#7077FE',
                                fontSize: '13px'
                              }}
                            >
                              <ExternalLink size={14} />
                              {link.title || link.url}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Comments Section */}
                    <div>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MessageSquare size={16} /> Comments ({submission.comments?.length || 0})
                      </h4>
                      
                      {/* Comments List */}
                      {submission.comments && submission.comments.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                          {submission.comments.map(comment => {
                            const canDeleteComment = comment.user_id === user?.id || ['manager', 'ceo', 'superadmin'].includes(user?.role);
                            return (
                              <div 
                                key={comment.id}
                                style={{
                                  padding: '12px 16px',
                                  background: 'var(--bg-secondary)',
                                  borderRadius: '8px',
                                  borderLeft: '3px solid var(--border-color)'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <UserAvatar user={{ id: comment.user_id, full_name: comment.user_name }} size={28} />
                                    <div>
                                      <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>{comment.user_name}</span>
                                      <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                                        {new Date(comment.created_at).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                  {canDeleteComment && (
                                    <button
                                      onClick={() => handleDeleteComment(submission.id, comment.id)}
                                      style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.5' }}>
                                  {comment.content}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add Comment */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <input
                          type="text"
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddComment(submission.id)}
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            fontSize: '14px'
                          }}
                        />
                        <button
                          onClick={() => handleAddComment(submission.id)}
                          style={{
                            padding: '12px 20px',
                            background: '#FF9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: '600'
                          }}
                        >
                          <Send size={16} /> Comment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Submit/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm} data-testid="research-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Beaker size={24} color="#FF9800" />
                {editingSubmission ? 'Edit Research' : 'Submit Research'}
              </h2>
              <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Research Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Enter a descriptive title for your research"
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  data-testid="research-title-input"
                />
              </div>

              {/* Category */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Category</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                  {CATEGORIES.map(cat => {
                    const CatIcon = cat.icon;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat.value })}
                        style={{
                          padding: '12px',
                          border: `2px solid ${formData.category === cat.value ? cat.color : 'var(--border-color)'}`,
                          borderRadius: '8px',
                          background: formData.category === cat.value ? `${cat.color}15` : 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: formData.category === cat.value ? cat.color : 'var(--text-secondary)'
                        }}
                      >
                        <CatIcon size={18} />
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Research Description *</label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  placeholder="Describe your research in detail..."
                  minHeight="200px"
                />
              </div>

              {/* Tags */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Tags</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tags..."
                    style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    style={{ padding: '10px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-primary)' }}
                  >
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {formData.tags.map((tag, idx) => (
                      <span key={idx} style={{
                        padding: '4px 10px',
                        background: '#FF980020',
                        borderRadius: '16px',
                        fontSize: '12px',
                        color: '#FF9800',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF9800', padding: 0 }}>
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Document Links */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Reference Links</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={newLink.title}
                    onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                    placeholder="Link title"
                    style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  />
                  <input
                    type="url"
                    value={newLink.url}
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    placeholder="https://..."
                    style={{ flex: 2, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  />
                  <button
                    type="button"
                    onClick={addLink}
                    style={{ padding: '10px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-primary)' }}
                  >
                    Add
                  </button>
                </div>
                {formData.document_links.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {formData.document_links.map((link, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '6px'
                      }}>
                        <span style={{ fontSize: '13px', color: '#7077FE' }}>
                          <LinkIcon size={12} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                          {link.title}
                        </span>
                        <button type="button" onClick={() => removeLink(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                >
                  <option value="draft">Draft (Save for later)</option>
                  <option value="submitted">Submitted</option>
                  {['manager', 'ceo', 'superadmin'].includes(user?.role) && (
                    <>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="archived">Archived</option>
                    </>
                  )}
                </select>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px' }}
                  data-testid="research-submit-btn"
                >
                  {editingSubmission ? 'Update Research' : 'Submit Research'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchSubmissions;
